import { useRatebooks } from '@api/useFreight/useFreight'
import type { Ratebook } from '@api/useFreight/types'
import { useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import * as React from 'react'
import dayjs from 'dayjs'

export function useBillingStatusBanner(ratebook: Ratebook, initialStatus: string) {
  const { useSetRatebookHoldMutation, useRemoveRatebookHoldMutation, useCreateRatebookMutation } = useRatebooks()
  const setHoldMutation = useSetRatebookHoldMutation()
  const removeHoldMutation = useRemoveRatebookHoldMutation()
  const createRatebookMutation = useCreateRatebookMutation()

  const [currentStatus, setCurrentStatus] = React.useState(initialStatus)
  const [messageApi, contextHolder] = message.useMessage()
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false)
  const [selectedDay, setSelectedDay] = React.useState<string>(
    ratebook.invoice_configuration?.invoice_day_of_week || 'Monday'
  )

  const queryClient = useQueryClient()

  React.useEffect(() => {
    setCurrentStatus(initialStatus)
  }, [initialStatus])

  const handleActivate = async () => {
    if (!ratebook.invoice_configuration?.recipients?.length) {
      messageApi.error('Please add at least one contact before activating invoicing')
      return
    }

    const billingFrequency = ratebook.invoice_configuration?.billing_frequency

    try {
      const updatedRatebook = {
        ...ratebook,
        invoice_status: 'Active',
        invoice_configuration: {
          ...ratebook.invoice_configuration,
          ...(billingFrequency === 'Weekly' && {
            invoice_day_of_week: selectedDay,
          }),
          activation_date: dayjs().format('MM/DD/YYYY'),
        },
      }
      await createRatebookMutation.mutateAsync(updatedRatebook as Ratebook)
      queryClient.invalidateQueries({ queryKey: ['ratebook', ratebook._id] })
      setCurrentStatus('Active')
      messageApi.success('Invoicing activated successfully')
      setIsPopoverOpen(false)
    } catch (error) {
      console.error('Failed to activate invoicing:', error)
      messageApi.error('Failed to activate invoicing')
    }
  }

  const handleSetHold = async () => {
    try {
      if (!ratebook._id) {
        throw new Error('Ratebook ID is required')
      }
      await setHoldMutation.mutateAsync(ratebook._id)
      setCurrentStatus('Hold')
      messageApi.success('Invoicing put on hold')
    } catch (error) {
      console.error('Failed to set hold:', error)
      messageApi.error('Failed to put invoicing on hold')
    }
  }

  const handleRemoveHold = async () => {
    try {
      if (!ratebook._id) {
        throw new Error('Ratebook ID is required')
      }
      await removeHoldMutation.mutateAsync(ratebook._id)
      setCurrentStatus('Active')
      messageApi.success('Invoicing hold removed')
    } catch (error) {
      console.error('Failed to remove hold:', error)
      messageApi.error('Failed to remove invoicing hold')
    }
  }

  const statusConfig = {
    Active: {
      icon: 'CheckCircleOutlined',
      title: 'Invoicing Active',
      description: 'This customer is actively generating invoices.',
      action: 'Apply Hold',
      nextStatus: 'Hold',
      background: 'info' as const,
      confirmMessage: 'Put invoicing on hold?',
      handleConfirm: handleSetHold,
    },
    Hold: {
      icon: 'PauseOutlined',
      title: 'Invoicing On Hold',
      description: "This customer's invoicing is currently on hold.",
      action: 'Resume',
      nextStatus: 'Active',
      background: 'warning' as const,
      confirmMessage: 'Resume invoicing?',
      handleConfirm: handleRemoveHold,
    },
    'No Invoice': {
      icon: 'CloseOutlined',
      title: 'No Invoicing',
      description: 'This customer is not set up for invoicing.',
      action: 'Activate',
      nextStatus: 'Active',
      background: 'default' as const,
      confirmMessage: 'Activate invoicing?',
      handleConfirm: handleActivate,
    },
  }

  const config = statusConfig[currentStatus as keyof typeof statusConfig] || statusConfig['No Invoice']

  return {
    currentStatus,
    setCurrentStatus,
    isPopoverOpen,
    setIsPopoverOpen,
    selectedDay,
    setSelectedDay,
    statusConfig,
    config,
    contextHolder,
    handleActivate,
    handleSetHold,
    handleRemoveHold,
    createRatebookMutation,
    setHoldMutation,
    removeHoldMutation,
  }
}
